/**
 * MoveChallenge Component
 * 
 * Challenges users to make specific chess moves, with validation and hints.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ChessBoard from '../ChessBoard';
import NotationDisplay from '../NotationDisplay';
import ChessService from '../../../services/chess/chessService';
import FeedbackDisplay from '../../feedback/FeedbackDisplay';
import Timer from '../../common/Timer';
import { ChessMove, PieceType, FEN } from '../../../types/chess';
import { useProgress } from '../../../context/ProgressContext';
import styles from './MoveChallenge.module.css';
import { Chess, Move, PieceSymbol } from 'chess.js';
import { randomizePositions } from 'utils/chessRandomUtils';
import { formatTime } from 'utils/timeUtils';
import { sanToEnglish } from 'utils/sanToEnglish';
import { generateRandomSan } from '../../../utils/chessRandomUtils';
import { moveGeneratorStrategies } from '../../../services/chess/moveGenerators';
import { KnowledgeItem, SuperMemoGrade } from '../../../types/learning';
import { ChallengeResult, mapResultToSuperMemoGrade, handleChallengeComplete } from '../../../utils/learningGrading';
import { set } from 'date-fns';
import { useMissionDebugState } from '../../../hooks/useMissionDebugState';
import { useMissionDebugAction } from '../../../hooks/useMissionDebugAction';
import { describeMissionMoveSan, getCanonicalMissionMoveSan, resolveMissionMoveSan } from '../../../services/chess/missionMoveSan';



const DEBUG = false; // Disable debug logging
const log = (...args: any[]) => DEBUG && console.log('[MoveChallenge Debug]', ...args);
const logError = (...args: any[]) => DEBUG && console.error('[MoveChallenge Error]', ...args);


/**
 * Props for test mode configuration
 */
export interface TestModeMoveChallengeProps {
  disableTimer?: boolean;
  initialSAN?: string[];
  autoComplete?: boolean;
  forceInterference?: boolean;
  disableInterference?: boolean;
  feedbackDurationMs?: number;
}

/**
 * Props for the MoveChallenge component
 */
export interface MoveChallengeProps {
  /** Target score to pass the challenge */
  targetScore?: number;
  /** Iterations for the challenge */
  challengeCount?: number;
  /** Mission ID for the challenge */
  missionId: string;
  /** Mission title for the challenge */
  missionTitle: string;
  /**
   * Called when the challenge is completed. Deprecated: use onComplete(grade) instead.
   */
  onCompleteLegacy?: (
    success: boolean,
    score: number,
    accuracy: number,
    movesUsed?: number,
    hintsUsed?: number,
    timeSpent?: number
  ) => void;
  /**
   * Called with the SuperMemo grade and optional timeSpent when the challenge is complete.
   */
  onComplete?: (grade: SuperMemoGrade, timeSpent?: number) => void;
  /**
   * The knowledge item for this challenge (optional).
   */
  item?: KnowledgeItem;
  /**
   * Optional override for grading logic.
   */
  gradeMapper?: (result: ChallengeResult) => SuperMemoGrade;
  /** Title for the challenge */
  title?: string;
  /** Test mode props for the challenge */
  testModeProps?: TestModeMoveChallengeProps;
  /** Randomize pieces on success */
  randomizePiecesOnSuccess?: boolean;
  /** FEN string for initial position */
  position: FEN;
  /** Target move in SAN notation */
  targetMove: string;
  /** Optional time limit in seconds */
  timeLimit?: number;
  /** Difficulty level (1-5) */
  difficulty?: number;
  /** Maximum number of moves attempted to get full score */
  maxMoves?: number;
  /** Pieces to use in the challenge */
  pieces?: PieceSymbol[];
  /** Optional focus key for generator strategy */
  focus?: string;
}

// Add this helper function for validating FEN strings
const validateFEN = (fen: string): string => {
  if (!fen) return new Chess().fen(); // Default position if no FEN provided
  
  try {
    const parts = fen.split(' ');
    if (parts.length !== 6) return fen; // Not standard FEN format
    
    // Check and fix the en-passant square (4th component)
    const enPassantIndex = 3;
    const enPassant = parts[enPassantIndex];
    
    // If it's not "-" and not a valid square (a-h)(1-8), replace with "-"
    if (enPassant !== '-' && !/^[a-h][1-8]$/.test(enPassant)) {
      // Invalid en-passant square, replace with "-"
      parts[enPassantIndex] = '-';
      return parts.join(' ');
    }
    
    return fen;
  } catch (error) {
    console.error('Error validating FEN:', error);
    return new Chess().fen(); // Default position on error
  }
};

/**
 * MoveChallenge - Challenges users to make a specific SAN chess move
 */
export const MoveChallenge: React.FC<MoveChallengeProps> = ({
  timeLimit = 60,
  targetScore = 10,
  position,
  targetMove,
  difficulty = 3,
  onCompleteLegacy,
  onComplete,
  item,
  gradeMapper,
  missionId,
  testModeProps,
  randomizePiecesOnSuccess,
  pieces,
  missionTitle,
  focus
}) => {
    // Game state
    const [currentTargetMove, setCurrentTargetMove] = useMissionDebugState<string>('', 'currentTargetMove', { label: 'Current Target Move', type: 'string' });
    const [usedCoordinates, setUsedCoordinates] = useState<Set<string>>(new Set());
    const [score, setScore] = useState<number>(0);
    const [timeRemaining, setTimeRemaining] = useMissionDebugState<number>(timeLimit, 'timeRemaining', { label: 'Time Remaining', type: 'number' });
    const [gameComplete, setGameComplete] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [currentFen, setCurrentFen] = useMissionDebugState<string>(validateFEN(new Chess().fen()), 'currentFen', { label: 'Current FEN', type: 'string' });
    
    // Feedback
    const [feedback, setFeedback] = useState<string>('');
    const [feedbackType, setFeedbackType] = useState<'correct' | 'incorrect' | null>(null);
    
    // Square highlight for clicked square
    const [highlightSquare, setHighlightSquare] = useState<string | undefined>(undefined);
    
    // Interference effects (for advanced mode)
    const [interference, setInterference] = useState<boolean>(false);
    const [scrambled, setScrambled] = useState<boolean>(false);
    const [boardDistortion, setBoardDistortion] = useState<boolean>(false);
    const [showHuman, setShowHuman] = useState<boolean>(true);
  
    // Cooldown to prevent rapid clicking
    const cooldownRef = useRef<boolean>(false);

    // Chess instance
    const chess = new Chess();
    
    // Add state for tracking attempts and hint
    const [attempts, setAttempts] = useState<number>(0);
    const [hint, setHint] = useState<string>(''); // Hint to display to the user
    const [wrongAttempts, setWrongAttempts] = useState<number>(0);
    const [totalMovesAttempted, setTotalMovesAttempted] = useState<number>(0);
    const [hintsShown, setHintsShown] = useState<number>(0);

    // Track the current round (0 = first, >0 = subsequent)
    const [round, setRound] = useState<number>(0);

    // Mission-specific fallback for move generators: always a valid board state and move
    const guaranteedPosition = useMemo(() => ({
      position: position.slice(),
      targetMove: targetMove.slice(),
      isFallback: true
    }), [position, targetMove]);

    // Generator strategy pattern: use focus if provided and strategy exists.
    // Avoid depending on usedCoordinates here since this effect writes it.
    useEffect(() => {
      if (round === 0) {
        // First round always uses mission's position and targetMove.
        setCurrentFen(position);
        setCurrentTargetMove(getCanonicalMissionMoveSan(position, targetMove));
        setUsedCoordinates(new Set([targetMove]));
        return;
      }

      if (focus && moveGeneratorStrategies[focus]) {
        // Subsequent rounds for focus missions use strategy-generated scenarios.
        const generated = moveGeneratorStrategies[focus](guaranteedPosition);
        setCurrentFen(generated.position);
        setCurrentTargetMove(getCanonicalMissionMoveSan(generated.position, generated.targetMove));
        setUsedCoordinates(new Set([generated.targetMove]));
        return;
      }

      // Piece-based fallback uses latest usedCoordinates via functional update.
      setUsedCoordinates((previous) => {
        const newSan = generateRandomSan(previous, pieces);
        setCurrentFen(newSan.fen);
        setCurrentTargetMove(getCanonicalMissionMoveSan(newSan.fen, newSan.san));
        return new Set([newSan.san]);
      });
    }, [focus, position, targetMove, round, guaranteedPosition, pieces, setCurrentFen, setCurrentTargetMove]);

    const resolvedTargetMove = useMemo(
      () => resolveMissionMoveSan(currentFen as FEN, currentTargetMove),
      [currentFen, currentTargetMove]
    );
    const displayedTargetMove = resolvedTargetMove.displaySan ?? getCanonicalMissionMoveSan(currentFen as FEN, currentTargetMove);
    const displayedTargetMoveHuman = describeMissionMoveSan(currentFen as FEN, currentTargetMove);

    const getMissionMetrics = useCallback((
      correctCount: number,
      attemptedMoves: number,
      wrongCount: number,
      hintsCount: number
    ) => {
      const attemptsForAccuracy = Math.max(1, attemptedMoves);
      const accuracy = (correctCount / attemptsForAccuracy) * 100;
      const baseScore = targetScore > 0 ? (correctCount / targetScore) * 100 : 0;
      const missionScore = Math.max(0, Math.min(100, Math.round(baseScore - wrongCount * 4 - hintsCount * 6)));
      return { missionScore, accuracy: Number(accuracy.toFixed(1)) };
    }, [targetScore]);

    // Timer effect
    useEffect(() => {
      if (gameComplete || (testModeProps?.disableTimer)) return;
      
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            const didSucceed = score >= targetScore;
            setGameComplete(true);
            setSuccess(didSucceed);
            const result: ChallengeResult = { correct: didSucceed, attempts: score, total: targetScore };
            const grade = (gradeMapper ?? mapResultToSuperMemoGrade)(result);
            const timeSpent = timeLimit - prev + 1;
            const { missionScore, accuracy } = getMissionMetrics(score, totalMovesAttempted, wrongAttempts, hintsShown);
            if (onCompleteLegacy) {
              onCompleteLegacy(didSucceed, missionScore, accuracy, totalMovesAttempted, hintsShown, timeSpent);
            }
            if (onComplete) onComplete(grade, timeLimit - timeRemaining);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }, [gameComplete, targetScore, score, testModeProps, gradeMapper, onComplete, onCompleteLegacy, setTimeRemaining, timeLimit, timeRemaining, getMissionMetrics, totalMovesAttempted, wrongAttempts, hintsShown]);

    useEffect(() => {
      if (testModeProps?.disableInterference) return;

      let interferenceRate = 0.95;
      let interferenceDuration = 2000;
      let interferenceTiming = 500;
      let boardDistortionDuration = 2500;
      let boardDistortionTiming = 3000;
      let boardDistortionRate = 0.6;

      // Disable interference effects for mission-101
      switch (missionId) {
        case 'piece-movement-101':
          interferenceRate = 0.0;
          boardDistortionRate = 0.0;
          setShowHuman(true);
          break;
        case 'piece-movement-102':
          interferenceRate *= 0.1;
          boardDistortionRate *= 0.1;
          setShowHuman(true);
          break;
        case 'piece-movement-103':
          interferenceRate *= 0.3;
          boardDistortionRate *= 0.3;
          setShowHuman(true);
          break;
        case 'piece-movement-104':
          interferenceRate *= 0.6;
          boardDistortionRate *= 0.6;
          interferenceDuration *= 1.5;
          boardDistortionDuration *= 1.5;
          setShowHuman(false);
          break;
        case 'piece-movement-105':
          interferenceRate *= 1.0;
          boardDistortionRate *= 1.0;
          interferenceDuration *= 2.0;
          boardDistortionDuration *= 2.0;
          setShowHuman(false);
          break;
        case 'piece-movement-106':
          interferenceRate *= 1.5;
          boardDistortionRate *= 1.5;
          interferenceDuration *= 2.5;
          boardDistortionDuration *= 2.5;
          interferenceTiming /= 1.5;
          boardDistortionTiming /= 1.5;
          setShowHuman(false);
          break;
        case 'piece-movement-107':
          interferenceRate *= 2.0;
          boardDistortionRate *= 2.0;
          interferenceDuration *= 3.0;
          boardDistortionDuration *= 3.0;
          interferenceTiming /= 2.0;
          boardDistortionTiming /= 2.0;
          setShowHuman(false);
          break;
        default:
          break;
      }

      
      // Ensure we apply effects regardless of random condition during initial load
      // to make sure they're visible for testing
      setInterference(true);
      setTimeout(() => setInterference(false), interferenceDuration);
      
      // Also show scrambled effect on initial load
      setScrambled(true);
      setTimeout(() => setScrambled(false), interferenceDuration - interferenceTiming);
      
      // Show initial board distortion effect - extended duration
      setBoardDistortion(true);
      setTimeout(() => setBoardDistortion(false), interferenceDuration + interferenceTiming);
      
      // Randomly apply interference effects
      const interferenceInterval = setInterval(() => {
        if (Math.random() < interferenceRate) {
          setInterference(true);
          setTimeout(() => setInterference(false), interferenceDuration - interferenceTiming);
        }
      }, interferenceTiming + Math.random() * interferenceTiming);
      
      // Randomly scramble coordinates - more frequently (every 3-4 seconds instead of 7)
      const scrambleInterval = setInterval(() => {
        // Increase probability from 0.5 to 0.7 to make scrambling happen more often
        if (Math.random() < interferenceRate) {
          setScrambled(true);
          setTimeout(() => setScrambled(false), interferenceTiming); // Shorter duration for more frequent glitches
        }
      }, interferenceTiming + Math.random() * interferenceTiming); // Random interval between 3-4 seconds
      
      // Randomly apply board distortion effects - increased frequency and duration
      const boardDistortionInterval = setInterval(() => {
        if (Math.random() < boardDistortionRate) { // Increased probability from 0.3 to 0.6
          setBoardDistortion(true);
          setTimeout(() => setBoardDistortion(false), boardDistortionDuration); // Increased duration
        }
      }, boardDistortionTiming + Math.random() * boardDistortionDuration); // Decreased interval: 3-5 seconds (from 6-8)
      
      return () => {
        clearInterval(interferenceInterval);
        clearInterval(scrambleInterval);
        clearInterval(boardDistortionInterval);
      };
    }, [missionId, testModeProps?.disableInterference]);
    
    // Handle test mode auto-complete
    useEffect(() => {
      if (testModeProps?.autoComplete && score >= targetScore) {
        setGameComplete(true);
        setSuccess(true);
        const result: ChallengeResult = { correct: true, attempts: score, total: targetScore };
        const grade = (gradeMapper ?? mapResultToSuperMemoGrade)(result);
        handleChallengeComplete({
          success: true,
          score,
          total: targetScore,
          movesUsed: score,
          gradeMapper,
          onComplete,
          onCompleteLegacy
        });
        if (onComplete) onComplete(grade, timeLimit - timeRemaining);
      }
    }, [score, targetScore, testModeProps, gradeMapper, onComplete, onCompleteLegacy, timeLimit, timeRemaining]);  


    // Mission-specific title
    const getMissionTitle = () => {
      return missionTitle;
    };

    const getShouldMissionBeRandomized = () => {
      return true;
    };
    

    // Force interference effects for testing using testModeProps
    useEffect(() => {
      if (testModeProps?.forceInterference) {
        setInterference(true);
        setTimeout(() => setInterference(false), testModeProps?.feedbackDurationMs ?? 2000);
      }
    }, [testModeProps?.forceInterference, testModeProps?.feedbackDurationMs]);

    // Add effect to show a hint after 2 failed attempts
    useEffect(() => {
      if (attempts === 2) {
        setHint('Hint: Review the target move and try to match the notation.');
        setHintsShown(prev => prev + 1);
      } else if (attempts > 2) {
        setHint('Hint: Pay attention to the piece and destination square.');
      } else if (attempts === 0) {
        setHint('');
      }
    }, [attempts]);

    return (
      <div 
        className={`${styles.container} ${interference ? styles.interference : ''}`}
        data-testid="mission-challenge-container"
        style={{ position: 'relative' }}
      >
        <div className={styles.header}>
          <h2>{getMissionTitle()}</h2>
          <div className={styles.stats}>
            <span className={styles.score}>Score: {score}/{targetScore}</span>
            <span className={styles.timer}>Time: {formatTime(timeRemaining)}</span>
          </div>
        </div>
        
        {!gameComplete ? (
          <>
            <div className={styles.target}>
              <p>Perform this move:</p>
                <span 
                className={`${styles.coordinate} ${scrambled ? styles.scrambled : ''}`}
                data-testid="target-move" 
                data-content={displayedTargetMove}
              >
                {displayedTargetMove} {currentFen.match(/ w /) ? 'w' : 'b'}
              </span>
              <span 
                className={`${styles.coordinatesecret} ${scrambled ? styles.scrambled : ''}`}
                data-testid="target-move-human" 
                data-content={displayedTargetMoveHuman}
              >
                {showHuman ? displayedTargetMoveHuman : '[hidden]'}
              </span>
            </div>
            
            <div className={`${styles.boardContainer} ${boardDistortion ? styles.boardDistortion : ''}`}
                 data-testid="board-container">
              <ChessBoard 
                size={Math.min(400, window.innerWidth - 40)}
                fen={currentFen}
                showCoordinates={true}
                allowMoves={true}
                highlightSquare={highlightSquare}
                /**
                 * Handle user move on the chess board
                 * @param from - The starting square
                 * @param to - The destination square
                 * @param promotion - Optional promotion piece
                 */
                onMove={(from: string, to: string, promotion?: PieceType) => {
                  if (gameComplete) return;
                  let oldFen = currentFen.slice();
                  setTotalMovesAttempted(prev => prev + 1);
                  // Use a new Chess instance to validate the move
                  const chessInstance = new Chess(validateFEN(currentFen));
                  const move = chessInstance.move({ from, to, promotion });
                  if (!move) {
                    setFeedback('Invalid move.');
                    setFeedbackType('incorrect');
                    setHighlightSquare(`${to}:incorrect`);
                    setAttempts(prev => prev + 1);
                    setWrongAttempts(prev => prev + 1);
                    return;
                  }
                  // Compare SAN to targetMove
                  if (DEBUG) {
                    console.log('move.san', move.san);
                    console.log('currentTargetMove', currentTargetMove);
                  }
                  setCurrentFen(chessInstance.fen());
                  if (DEBUG) {
                    console.log('currentFen.chessInstance', currentFen);
                    console.log('fen.chessInstance', chessInstance.fen());
                  }
                  const isCorrectMove =
                    resolvedTargetMove.isValid
                    && move.from === resolvedTargetMove.from
                    && move.to === resolvedTargetMove.to
                    && (move.promotion ?? undefined) === resolvedTargetMove.promotion;

                  if (isCorrectMove) {
                    const newScore = score + 1;
                    setScore(newScore);
                    setFeedback('Correct move! Well done!');
                    setFeedbackType('correct');
                    setHighlightSquare(`${to}:correct`);
                    setAttempts(0); // Reset attempts on success
                    setHint(''); // Clear hint on success
                    // If challenge is complete, finish
                    if (newScore >= targetScore) {
                      setGameComplete(true);
                      setSuccess(true);
                      const finalAttempted = totalMovesAttempted + 1;
                      const { missionScore, accuracy } = getMissionMetrics(newScore, finalAttempted, wrongAttempts, hintsShown);
                      const timeSpent = timeLimit - timeRemaining;
                      if (onCompleteLegacy) {
                        onCompleteLegacy(true, missionScore, accuracy, finalAttempted, hintsShown, timeSpent);
                      }
                      let result: ChallengeResult = { correct: true, attempts: newScore, total: targetScore };
                      if (onComplete && gradeMapper) onComplete(gradeMapper(result), timeLimit - timeRemaining);
                    } else {
                      // Not complete: randomize board and select new SAN
                      setTimeout(() => {
                        // For focus missions, increment round to trigger generator after first round
                        setRound(prev => prev + 1);
                        // For piece-based missions, generate new SAN, passing the pieces provided in props and update state for next round
                        if (!focus || !moveGeneratorStrategies[focus]) {

                          setFeedback('');
                          setFeedbackType(null);
                          setHighlightSquare(undefined);
                          setHint(''); // Clear hint for new round
                          setAttempts(0); // Reset attempts for new round

                        } else {
                          // For focus missions, useEffect will handle updating state
                          setFeedback('');
                          setFeedbackType(null);
                          setHighlightSquare(undefined);
                          setHint('');
                          setAttempts(0);
                        }
                      }, testModeProps?.feedbackDurationMs ?? 1000); // Short delay for feedback UX
                    }
                  } else {
                    setFeedback("That's not the best move. Try again.");
                    setFeedbackType('incorrect');
                    setHighlightSquare(`${to}:incorrect`);
                    setAttempts(prev => prev + 1);
                    setWrongAttempts(prev => prev + 1);
                    setTimeout(() => {
                      setFeedback('');
                      setFeedbackType(null);
                      setHighlightSquare(undefined);
                      setCurrentFen(oldFen);
                    }, testModeProps?.feedbackDurationMs ?? 1000);
                  }
                }}
              />
            </div>
            
            {feedback && (
              <div
                className={`${styles.feedback} ${styles[feedbackType || '']}`}
                data-testid="mock-feedback"
              >
                {feedback}
              </div>
            )}
          </>
        ) : (
          <div className={styles.completion}>
            <h3>{success ? 'Challenge Completed!' : 'Challenge Failed!'}</h3>
            <p>You scored {score} out of {targetScore} points</p>
            {!success && <p>Try again to improve your score!</p>}
          </div>
        )}

        {/* Add a placeholder for hint rendering with data-testid="mock-hint" */}
        {hint && (
          <div data-testid="mock-hint">{hint}</div>
        )}
      </div>
    );

};

export default MoveChallenge; 
